import { collection, addDoc, getDocs, query, where, doc, updateDoc, getDoc, orderBy, deleteDoc, writeBatch } from 'firebase/firestore';
import { db } from '../firebase';

const deleteOrder = async (orderId) => {
  try {
    // Get the order first to check if it exists and get customer data if needed
    const orderRef = doc(db, 'orders', orderId);
    const orderDoc = await getDoc(orderRef);
    
    if (!orderDoc.exists()) {
      throw new Error('Order not found');
    }

    // Delete the order document
    await deleteDoc(orderRef);
    
    return true;
  } catch (error) {
    console.error('Error in deleteOrder:', error);
    throw error;
  }
};

async function updateOrderWithBulkPayment(orderId, updateData, customerId) {
  try {
    const batch = writeBatch(db);
    
    // Get all orders with pending balance for this customer
    const orders = await salesService.getOrdersByCustomer(customerId);
    const pendingOrders = orders
      .filter(o => (o.remainingBalance || 0) > 0)
      .sort((a, b) => new Date(a.finalizedAt) - new Date(b.finalizedAt));

    let remainingAmount = Number(updateData.amount);
    let totalBalanceChange = 0;

    // Update each order until the payment amount is exhausted
    for (const order of pendingOrders) {
      if (remainingAmount <= 0) break;

      const orderRef = doc(db, 'orders', order.id);
      const orderBalance = Number(order.remainingBalance) || 0;
      const amountToApply = Math.min(remainingAmount, orderBalance);
      
      if (amountToApply > 0) {
        const newAmountReceived = (Number(order.amountReceived) || 0) + amountToApply;
        const newRemainingBalance = orderBalance - amountToApply;
        
        batch.update(orderRef, {
          amountReceived: newAmountReceived,
          remainingBalance: newRemainingBalance,
          updatedAt: new Date().toISOString()
        });

        remainingAmount -= amountToApply;
        totalBalanceChange -= amountToApply;
      }
    }

    // Update customer balance
    const customerRef = doc(db, 'contacts', customerId);
    const customerDoc = await getDoc(customerRef);
    const customerData = customerDoc.data();

    batch.update(customerRef, {
      balance: customerData.balance + totalBalanceChange
    });

    // Commit all updates in a single transaction
    await batch.commit();

    return pendingOrders;
  } catch (error) {
    console.error('Error updating orders with bulk payment:', error);
    throw error;
  }
}

export const salesService = {
  async createOrder(orderData) {
    try {
      // First get customer details
      const customerDoc = await getDoc(doc(db, 'contacts', orderData.customerId));
      const customerData = customerDoc.data();

      const docRef = await addDoc(collection(db, 'orders'), {
        ...orderData,
        customerName: customerData.name,
        status: 'pending',
        createdAt: new Date().toISOString(),
      });
      return { id: docRef.id, ...orderData, customerName: customerData.name };
    } catch (error) {
      console.error('Error creating order:', error);
      throw error;
    }
  },

  async finalizeSale(orderId, saleData) {
    try {
      // Get the order to get customerId
      const orderDoc = await getDoc(doc(db, 'orders', orderId));
      const orderData = orderDoc.data();

      // Update the order
      const orderRef = doc(db, 'orders', orderId);
      await updateDoc(orderRef, {
        ...saleData,
        status: 'completed',
        finalizedAt: new Date().toISOString(),
      });

      // Get current customer data
      const customerRef = doc(db, 'contacts', orderData.customerId);
      const customerDoc = await getDoc(customerRef);
      const customerData = customerDoc.data();

      // Calculate net crates change
      const netCratesChange = saleData.cratesGiven - saleData.cratesReturned;

      // Update customer balance and crates
      await updateDoc(customerRef, {
        balance: (customerData.balance || 0) + saleData.remainingBalance,
        cratesOut: (customerData.cratesOut || 0) + netCratesChange
      });

      return { id: orderId, ...saleData };
    } catch (error) {
      console.error('Error finalizing sale:', error);
      throw error;
    }
  },

  getTodaysOrders: async () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const ordersRef = collection(db, 'orders');
    const q = query(
      ordersRef,
      where('createdAt', '>=', today)
    );
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  },

  getAllPendingOrders: async () => {
    const ordersRef = collection(db, 'orders');
    const q = query(
      ordersRef,
      where('status', '==', 'pending')
    );
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  },

  async getOrdersByCustomer(customerId) {
    try {
      const q = query(
        collection(db, 'orders'),
        where('customerId', '==', customerId)
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting customer orders:', error);
      throw error;
    }
  },

  async getAllSales() {
    try {
      // Get all orders, both pending and completed
      const ordersRef = collection(db, 'orders');
      const querySnapshot = await getDocs(ordersRef);
      
      const orders = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        // Ensure dates are properly handled
        createdAt: doc.data().createdAt,
        finalizedAt: doc.data().finalizedAt
      }));

      // Sort by finalizedAt date, most recent first
      return orders.sort((a, b) => {
        if (!a.finalizedAt) return 1;  // Push pending orders to the end
        if (!b.finalizedAt) return -1;
        return new Date(b.finalizedAt) - new Date(a.finalizedAt);
      });
    } catch (error) {
      console.error('Error getting all sales:', error);
      throw error;
    }
  },

  async updateOrder(orderId, updateData) {
    try {
      // Get the current order
      const orderRef = doc(db, 'orders', orderId);
      const orderDoc = await getDoc(orderRef);
      const orderData = orderDoc.data();

      // Calculate new remaining balance
      const newRemainingBalance = orderData.totalAmount - updateData.amountReceived;

      // Update the order
      await updateDoc(orderRef, {
        ...updateData,
        remainingBalance: newRemainingBalance,
        updatedAt: new Date().toISOString(),
      });

      // Update customer balance
      const customerRef = doc(db, 'contacts', orderData.customerId);
      const customerDoc = await getDoc(customerRef);
      const customerData = customerDoc.data();

      // Calculate balance difference
      const oldBalance = orderData.remainingBalance;
      const balanceDifference = newRemainingBalance - oldBalance;

      // Update customer balance
      await updateDoc(customerRef, {
        balance: customerData.balance + balanceDifference,
        cratesOut: customerData.cratesOut - (updateData.cratesReturned - (orderData.cratesReturned || 0))
      });

      return {
        id: orderId,
        ...orderData,
        ...updateData,
        remainingBalance: newRemainingBalance
      };
    } catch (error) {
      console.error('Error updating order:', error);
      throw error;
    }
  },

  async getOrder(orderId) {
    try {
      const orderDoc = await getDoc(doc(db, 'orders', orderId));
      if (!orderDoc.exists()) {
        throw new Error('Order not found');
      }
      return { id: orderDoc.id, ...orderDoc.data() };
    } catch (error) {
      console.error('Error getting order:', error);
      throw error;
    }
  },

  getOrdersWithBalance: async () => {
    const ordersRef = collection(db, 'orders');
    // We can't filter by remainingBalance in Firestore, so we'll fetch all completed orders
    const q = query(
      ordersRef,
      where('status', '==', 'completed')
    );
    
    const snapshot = await getDocs(q);
    return snapshot.docs
      .map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
      .filter(order => {
        const remainingBalance = Number(order.remainingBalance) || 
          (order.totalAmount && order.amountReceived ? 
            Number(order.totalAmount) - Number(order.amountReceived) : 
            0);
        return remainingBalance > 0;
      });
  },

  deleteOrder,
  updateOrderWithBulkPayment
};